/**
 * Authentication Context
 * Manages user authentication state via Canva OAuth
 */
import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { intervalManager } from '../utils/intervalManager';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const API_BASE_URL = process.env.REACT_APP_API_URL || (
    window.location.hostname === 'localhost'
      ? 'http://localhost:8000/api'
      : '/api'
  );

  const fetchUserData = useCallback(async (token) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const userData = await response.json();
        setUser({ ...userData, authenticated: true, canva_connected: userData.canva_connected ?? false });
        return true;
      } else {
        localStorage.removeItem('access_token');
        setUser(null);
        return false;
      }
    } catch (err) {
      console.error('Failed to fetch user data:', err);
      localStorage.removeItem('access_token');
      setUser(null);
      return false;
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    // Check localStorage first; also pick up token from URL
    // (handles /complete-profile?token=xxx redirect from Canva OAuth)
    let token = localStorage.getItem('access_token');
    if (!token) {
      const urlToken = new URLSearchParams(window.location.search).get('token');
      if (urlToken) {
        token = urlToken;
        localStorage.setItem('access_token', token);
      }
    }
    if (token) {
      fetchUserData(token).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [fetchUserData]);

  const loginWithToken = useCallback((token) => {
    localStorage.setItem('access_token', token);
    fetchUserData(token);
    // Notify sidebar to show "Just now" — backend triggers ETL on every login
    window.dispatchEvent(new CustomEvent('etl:started'));
  }, [fetchUserData]);

  const logout = useCallback(async () => {
    intervalManager.clearAll();
    try {
      await fetch(`${API_BASE_URL}/auth/canva/revoke`, { method: 'POST' });
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
      localStorage.removeItem('access_token');
    }
  }, [API_BASE_URL]);

  const refreshUser = useCallback(() => {
    const token = localStorage.getItem('access_token');
    if (token) return fetchUserData(token);
    return Promise.resolve(false);
  }, [fetchUserData]);

  const value = {
    user,
    loading,
    loginWithToken,
    logout,
    refreshUser,
    isAuthenticated: !!user,
    needsProfileCompletion: !!user && !user.full_name,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
